import { useState } from 'react';
import type { LoteImport, TipoImport } from '@pcp/contracts';
import { useAplicarImport, useCriarImport, useImports, useTemplateImport } from '../api/importacao';
import { ApiRequestError } from '../api/client';
import { ErrorState, LoadingBlock } from '../components/states';
import { formatDateTime } from '../format';
import { t } from '../i18n/pt-BR';
import { buildTemplateBlob, csvToLines, downloadBlob, fileToLines } from '../lib/spreadsheet';

const i = t.importPage;
const TIPOS: { value: TipoImport; label: string }[] = [
  { value: 'CAPACIDADE', label: i.types.capacity },
  { value: 'CALENDARIO', label: i.types.calendar },
  { value: 'PROGRAMACAO', label: i.types.schedule },
];
const TIPO_LABEL = Object.fromEntries(TIPOS.map((x) => [x.value, x.label])) as Record<TipoImport, string>;

const SITUACAO = {
  1: { label: i.status.pending, tone: 'info' },
  2: { label: i.status.valid, tone: 'ok' },
  3: { label: i.status.error, tone: 'bad' },
  4: { label: i.status.applied, tone: 'info' },
} as const;

const errorText = (e: unknown) =>
  e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : e ? t.states.datasulDown : null;

/** Importar Excel: modelo → arquivo → validação linha a linha → aplicar só as validadas → histórico. */
export function ImportPage() {
  const [tipo, setTipo] = useState<TipoImport>('CAPACIDADE');
  const [fileName, setFileName] = useState('');
  const [lines, setLines] = useState<string[]>([]);
  const [paste, setPaste] = useState('');
  const [readError, setReadError] = useState<string | null>(null);
  const [batch, setBatch] = useState<LoteImport | null>(null);
  const [applied, setApplied] = useState<{
    aplicadas: number;
    inseridas: number;
    atualizadas: number;
  } | null>(null);

  const template = useTemplateImport();
  const criar = useCriarImport();
  const aplicar = useAplicarImport();
  const history = useImports('');

  const onFile = async (file: File | undefined) => {
    setReadError(null);
    setBatch(null);
    setApplied(null);
    if (!file) return;
    try {
      const rows = await fileToLines(file);
      setFileName(file.name);
      setLines(rows);
      setPaste('');
    } catch (e) {
      setLines([]);
      setReadError(errorText(e));
    }
  };

  const onPaste = (text: string) => {
    setPaste(text);
    setBatch(null);
    setApplied(null);
    setFileName('colado.csv');
    setLines(csvToLines(text));
  };

  const download = async () => {
    const tpl = await template.mutateAsync(tipo);
    downloadBlob(await buildTemplateBlob(tpl), `modelo-${tipo.toLowerCase()}.xlsx`);
  };

  const validar = async () => {
    setApplied(null);
    setBatch(
      await criar.mutateAsync({
        'tipo-import': tipo,
        'nome-arquivo': fileName || 'importacao.csv',
        linhas: lines,
      }),
    );
  };

  const aplicarValidadas = async () => {
    if (!batch) return;
    const res = await aplicar.mutateAsync(batch['nr-import']);
    setApplied(res);
    // mostra o lote já com as linhas aplicadas
    const page = await history.refetch();
    const fresh = page.data?.items.find((b) => b['nr-import'] === batch['nr-import']);
    if (fresh) setBatch(fresh);
  };

  const dataLines = Math.max(0, lines.length - 1);

  return (
    <>
      <section className="panel" aria-labelledby="imp-title">
        <h2 id="imp-title">{t.nav.import}</h2>
        <p className="hint">{i.hint}</p>

        <div className="stack">
          <div className="row">
            <label className="inline-label" htmlFor="imp-tipo">
              {i.type}
            </label>
            <select
              id="imp-tipo"
              className="select"
              value={tipo}
              onChange={(e) => {
                setTipo(e.target.value as TipoImport);
                setBatch(null);
                setApplied(null);
              }}
            >
              {TIPOS.map((x) => (
                <option key={x.value} value={x.value}>
                  {x.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn ghost"
              onClick={() => void download()}
              disabled={template.isPending}
            >
              {i.downloadTemplate}
            </button>
          </div>
          {template.error ? (
            <div className="form-error" role="alert">
              {errorText(template.error)}
            </div>
          ) : null}

          <div className="field" style={{ maxWidth: 520 }}>
            <label htmlFor="imp-file">{i.file}</label>
            <input
              id="imp-file"
              type="file"
              accept=".xlsx,.csv,.txt"
              onChange={(e) => void onFile(e.target.files?.[0])}
            />
            <span className="hint">{i.fileHint}</span>
          </div>
          {readError ? (
            <div className="form-error" role="alert">
              {readError}
            </div>
          ) : null}

          <div className="field">
            <label htmlFor="imp-paste">{i.paste}</label>
            <textarea
              id="imp-paste"
              className="paste"
              rows={5}
              value={paste}
              placeholder={i.pastePlaceholder}
              onChange={(e) => onPaste(e.target.value)}
            />
          </div>

          <div className="row">
            <button
              type="button"
              className="btn"
              disabled={dataLines < 1 || criar.isPending}
              onClick={() => void validar()}
            >
              {criar.isPending ? i.validating : i.validate}
            </button>
            {lines.length ? <span className="hint">{i.readLines(dataLines, fileName)}</span> : null}
          </div>
          {criar.error ? (
            <div className="form-error" role="alert">
              {errorText(criar.error)}
            </div>
          ) : null}
        </div>
      </section>

      {batch ? (
        <section className="panel" aria-labelledby="imp-result">
          <h2 id="imp-result">{i.validation}</h2>
          <div className="row">
            <span className="chip ok">{i.validCount(batch.validas)}</span>
            <span className={`chip ${batch.erros ? 'bad' : 'info'}`}>{i.errorCount(batch.erros)}</span>
            <button
              type="button"
              className="btn"
              disabled={batch.validas === 0 || aplicar.isPending || !!applied}
              onClick={() => void aplicarValidadas()}
            >
              {i.apply}
            </button>
          </div>
          {aplicar.error ? (
            <div className="form-error" role="alert">
              {errorText(aplicar.error)}
            </div>
          ) : null}
          {applied ? (
            <div className="banner ok" role="status">
              {i.result(applied.aplicadas, applied.inseridas, applied.atualizadas)}
            </div>
          ) : null}
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th className="n">{i.cols.line}</th>
                  <th>{i.cols.content}</th>
                  <th>{i.cols.status}</th>
                  <th>{i.cols.message}</th>
                </tr>
              </thead>
              <tbody>
                {batch.linhas.map((l) => (
                  <tr key={l['num-linha']}>
                    <td className="n">{l['num-linha']}</td>
                    <td className="mono">{l.conteudo}</td>
                    <td>
                      <span className={`chip ${SITUACAO[l.situacao].tone}`}>
                        {SITUACAO[l.situacao].label}
                      </span>
                    </td>
                    <td className="msg">{l['msg-erro'] ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <section className="panel" aria-labelledby="imp-history">
        <h2 id="imp-history">{i.history}</h2>
        {history.isLoading ? <LoadingBlock rows={2} /> : null}
        {history.error ? <ErrorState error={history.error} onRetry={() => void history.refetch()} /> : null}
        {history.data && !history.data.items.length ? <p className="hint">{i.noHistory}</p> : null}
        {history.data?.items.length ? (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th className="n">{i.cols.number}</th>
                  <th>{i.type}</th>
                  <th>{i.cols.file}</th>
                  <th>{i.cols.user}</th>
                  <th>{i.cols.when}</th>
                  <th className="n">{i.cols.valid}</th>
                  <th className="n">{i.cols.errors}</th>
                </tr>
              </thead>
              <tbody>
                {history.data.items.map((b) => (
                  <tr key={b['nr-import']}>
                    <td className="n">{b['nr-import']}</td>
                    <td>{TIPO_LABEL[b['tipo-import']]}</td>
                    <td>{b['nome-arquivo']}</td>
                    <td className="mono">{b.usuario}</td>
                    <td className="mono">{formatDateTime(b['dt-hr-import'])}</td>
                    <td className="n">{b.validas}</td>
                    <td className="n">{b.erros}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </>
  );
}
